import { Box, Tabs, Text } from '@streamelements/frontend-ui';
import { ParsedLocation, useRouter, useRouterState } from '@tanstack/react-router';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CompositionsService } from '../../services/CompositionsService';
import { useEffect, useState } from 'react';
import { promisefy } from '../../utils/promisefy';

export const Route = createFileRoute('/_authenticated/settings/_layout')({
  component: SettingsLayout,
});

type Locations = {
    outputsSettings: ParsedLocation;
    createFirstCanvas: ParsedLocation;
    canvasSettings: ParsedLocation;
    broadcastSettings: ParsedLocation;
};

function SettingsLayout() {
  const routerState = useRouterState();
  const router = useRouter();
  const { itemId: outputId, canvasId } = Route.useSearch() as never;
  const { matches } = routerState;
  const { search } = matches[matches.length - 1];
  const { location: { pathname }} = routerState;

  const { data: videoCompositions } = useQuery({
    queryKey: ['video-compositions'],
    queryFn: async () => await CompositionsService.getActiveCompositions(),
  });

  const [ hasCustomCanvases, setHasCustomCanvases ] = useState<boolean>(false);
  const [ targetCanvasId, setTargetCanvasId ] = useState<string | undefined>(undefined);
  const [ locations, setLocations ] = useState<Locations | null>(null);

  useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      const { default: defaultComposition, ...others } = videoCompositions || ({} as never);
      
      const outputs = await promisefy(window.host.getAllStreamingOutputs)();

      if (abortController.signal.aborted) return;

      const selectedCanvasId = (() => {
        if (others?.[canvasId]) {
          return canvasId;
        }

        const outputCanvasId = outputs?.[outputId]?.videoCompositionId;

        if (others?.[outputCanvasId]) {
          return outputCanvasId;
        }

        return defaultComposition?.id ?? 'default';
      })();

      if (abortController.signal.aborted) return;

      setTargetCanvasId(selectedCanvasId);
      setHasCustomCanvases(!!selectedCanvasId);
      setLocations({
        outputsSettings: router.buildLocation({
          to: '/settings/outputs-settings',
          search: {
            canvasId: selectedCanvasId,
          },
        }),
        createFirstCanvas: router.buildLocation({
          to: '/settings/create-first-canvas',
        }),
        canvasSettings: router.buildLocation({
          to: '/settings/$canvasId',
          params: {
            canvasId: selectedCanvasId,
          },
          search: {
            canvasId: selectedCanvasId,
          },
        }),
        broadcastSettings: router.buildLocation({
          to: '/settings/broadcast-settings',
          search: {
            ...(search ?? {}),
            canvasId: selectedCanvasId as never, // TODO: type
          } as never,
        }),
      } as const);
    })();

    return () => {
      abortController.abort();
    };
  }, [search, videoCompositions, router, outputId, canvasId]);

  if (!locations) return null;

  return (
    <>
      <Box css={{ pt: '1.5rem', pb: '0.5rem', mb: '1rem', px: '1.5rem' }}>
        <Text.Heading css={{ m: 0 }} weight="bold" level={5}>
          Settings
        </Text.Heading>
        <Text.Body css={{ color: '$textPrimary50' }}>Configure your Multi-streaming settings</Text.Body>
      </Box>

      <Box
        as={Tabs.Root}
        value={pathname}
        onValueChange={async (value) => {
          const obj = {
            to: value,
            params: {
              canvasId: targetCanvasId,
            },
            search: {
              ...(search || {}),
              canvasId: targetCanvasId,
            },
          };

          router.navigate(obj);
        }}
        css={{ mb: '0.75rem' }}
      >
        <Tabs.List css={{ gap: '2rem', px: '1.5rem' }}>
          <Tabs.Trigger css={{ px: '0' }} value={locations.outputsSettings.pathname}>
            Outputs
          </Tabs.Trigger>
          <Tabs.Trigger
            css={{ px: '0' }}
            value={hasCustomCanvases ? locations.canvasSettings.pathname : locations.createFirstCanvas.pathname}
          >
            Canvas settings
          </Tabs.Trigger>
          <Tabs.Trigger css={{ px: '0' }} value={locations.broadcastSettings.pathname}>
            Broadcast settings
          </Tabs.Trigger>
        </Tabs.List>
      </Box>

      <Box css={{ flex: 1, overflow: 'hidden', pt: '0.125rem' }}>
        <Outlet />
      </Box>
    </>
  );
}
